<template>
  <promo-carousel v-if="promoCarouselFields" v-bind="promoCarouselFields" class="event-carousel">
    <template #pubInfo="{ slotFields }">
      <sc-text
        tag="p"
        :field="slotFields.pubAddress"
        class="event-carousel__pub-address swiper-no-swiping"
      />
      <div v-if="slotFields?.date" class="event-carousel__date swiper-no-swiping">
        <span>{{ t('event-listing-date') }}: </span>{{ slotFields.date }}
      </div>
      <div v-if="slotFields?.time" class="event-carousel__time swiper-no-swiping">
        <span>{{ t('event-listing-time') }}: </span>{{ slotFields.time }}
      </div>
    </template>
  </promo-carousel>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Text as ScText } from '@sitecore-jss/sitecore-jss-vue';
import { isEditorActive } from '@sitecore-jss/sitecore-jss/utils';
import PromoCarousel from '../PromoCarousel/PromoCarousel.vue';
import { formatDateRangeWithOrdinal, formatTimeRange } from '../../utils/dates';
import type { EventCarouselProps, EventCarouselItemFields } from './types';
import type { PromoCarouselProps } from '@/types';

const { t } = useI18n();

const props = withDefaults(defineProps<EventCarouselProps>(), {
  fields: () => ({}),
  params: () => ({}),
});

const promoCarouselFields = computed<PromoCarouselProps<EventCarouselItemFields>>(() => {
  return {
    params: { skipPlaceholder: true, contentAlign: props.params.contentAlign },
    rendering: {
      componentName: 'PromoCarousel',
      placeholders: {
        'promo-carousel-placeholder': props.fields.eventCards?.map(
          ({ fields }) =>
            (fields?.imageDesktop?.value?.src || isEditorActive()) && {
              componentName: 'PromoCarouselItem',
              dataSource: 'events_carousel',
              fields: {
                heading: fields.heading,
                pubAddress: fields.subheading,
                link: fields.secondLink,
                imageDesktop: fields.imageDesktop,
                imageTablet: fields.imageTablet,
                imageMobile: fields.imageMobile,
                date: formatDate(fields.startDate?.value, fields.endDate?.value),
                time: formatTime(
                  fields.startTime?.value,
                  fields.endTime?.value,
                  fields.startDate?.value,
                  fields.endDate?.value
                ),
              },
            }
        ),
      },
    },
    dataLayerComponentName: 'events_carousel',
  };
});

const formatDate = (startDate?: string, endDate?: string) => {
  if (!startDate || !endDate) return '';
  return formatDateRangeWithOrdinal(startDate, endDate);
};

const formatTime = (startTime?: string, endTime?: string, startDate?: string, endDate?: string) => {
  if (!startTime || !endTime) return '';

  let time = '';
  if (startDate !== endDate) {
    time += t('event-listing-every-day') + ' ';
  }
  return time + formatTimeRange(startTime, endTime);
};
</script>

<style lang="scss">
@import './scss/EventCarousel.scss';
</style>
